import React, { useState, useEffect, useMemo } from 'react';
import {
  Grid,
  CircularProgress,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
} from '@mui/material';
import { useHistory } from 'react-router-dom';
import GridContainer from '@jumbo/components/GridContainer';
import useManageMetadata from './hooks/useManageMetadata';
import customAxios from '../util/Api';
import authHeader from 'services/auth/jwt/auth-header';
import { DebugBoundary } from '../Dockets';
import { ColumnDirective, ColumnsDirective, Edit, Filter, GridComponent, Group, row } from '@syncfusion/ej2-react-grids';
import { Inject, Page, Sort, Search, Toolbar } from '@syncfusion/ej2-react-grids';

function MetadataSettings(props) {
  const pageSettings = useMemo(
    () => ({
      pageSize: 30,
      pageSizes: [5, 10, 20, 30, 40],
    }),
    [],
  );
  const filterSettings = {
    type: 'Excel',
  };
  const toolbarOptions = ['Search'];

  const {
    locations,
    loading,
    error,
    isModalOpen,
    isAddMode,
    openAddModal,
    openEditModal,
    closeModal,
    updateLocation,
    addLocation,
  } = useManageMetadata();

  const history = useHistory();
  const [formData, setFormData] = useState({});
  const [siteIds, setSiteIds] = useState([]);

  // 🟢 Fetch all site IDs for dropdown
  // 🟢 Fetch all site IDs (with labels)
  useEffect(() => {
    const fetchSiteIds = async () => {
      try {
        const res = await customAxios.get('/CRM/GetCRMSiteDetails', {
          headers: authHeader(),
        });

        if (Array.isArray(res.data)) {
          const formatted = res.data.map(item => ({
            id: item.siteAssessmentID,
            label: `${item.siteAssessmentID} : ${item.siteDetailsRef || 'N/A'}`,
          }));
          setSiteIds(formatted);
        }
      } catch (error) {
        console.error('Error fetching site IDs:', error);
      }
    };

    fetchSiteIds();
  }, []);

  // 🟣 When site ID is selected in ADD mode, fetch coordinates
  useEffect(() => {
    const fetchCoordinates = async () => {
      try {
        if (!formData.siteAssessmentId || !isAddMode) return;

        const res = await customAxios.get(
          `/SiteDetails/GetSiteDetailsMapCoordinates/${formData.siteAssessmentId}?mapType=pin`,
          { headers: authHeader() },
        );

        const coords = res.data?.[0];
        if (coords) {
          setFormData(prev => ({
            ...prev,
            latitude: coords.latitude ?? '',
            longitude: coords.longitude ?? '',
          }));
        }
      } catch (error) {
        console.error('Error fetching site coordinates:', error);
      }
    };

    fetchCoordinates();
  }, [formData.siteAssessmentId, isAddMode]);

  function locationsEditTemplate(rowData) {
    return (
      <Button
        variant="outlined"
        size="small"
        onClick={() => handleEditClick(rowData)}
        sx={{
          textTransform: 'none',
          fontWeight: 500,
          color: 'primary.main',
          borderColor: 'primary.main',
          borderRadius: '8px',
          px: 2,
          py: 0.5,
          '&:hover': {
            backgroundColor: 'primary.main',
            color: '#fff',
          },
        }}>
        Edit
      </Button>
    );
  }

  const handleEditClick = location => {
    setFormData({
      id: location.id,
      siteAssessmentId: location.siteAssessmentId,
      destinationName: location.destinationName,
      custCode: location.custCode,
      latitude: location.latitude,
      longitude: location.longitude,
      customerName: location.customerName,
    });
    openEditModal();
  };

  const handleInputChange = e => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSave = () => {
    if (isAddMode) {
      addLocation(formData);
    } else {
      updateLocation(formData);
    }
  };

  return (
    <div className="app-wrapper">
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <h2 style={{ fontSize: '1.5rem', margin: 0 }}>Manage Customers</h2>
        <Box display="flex" alignItems="center" gap={3}>
          <Button variant="contained" color="primary" onClick={openAddModal}>
            Add
          </Button>
          <Button variant="contained" color="primary" onClick={() => history.push('/')}>
            Home
          </Button>
        </Box>
      </Box>

      {/* Table */}
      <Box
        sx={{
          mt: 2,
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          overflow: 'hidden',
          backgroundColor: '#fff',
        }}>
        <DebugBoundary>
          <GridComponent
            dataSource={locations}
            filterSettings={filterSettings}
            allowPaging={true}
            pageSettings={pageSettings}
            allowSorting={true}
            allowTextWrap={true}
            allowResizing={true}
            allowMultiSorting={true}
            allowReordering={true}
            allowFiltering={true}
            style={{
              minHeight: 'auto !important',
              // opacity: isDocketDataLoading ? 0.5 : 1, // Dim the grid while loading
            }}
            height="100%"
            toolbar={toolbarOptions}>
            <ColumnsDirective>
              <ColumnDirective field="siteAssessmentId" headerText="Site Assessment" width="100" textAlign="center" />
              <ColumnDirective field="customerName" headerText="Customer Name" width="100" textAlign="center" />
              <ColumnDirective field="destinationName" headerText="Destination Name" width="100" textAlign="center" />
              <ColumnDirective field="custCode" headerText="Customer Code" width="80" textAlign="center" />
              <ColumnDirective field="latitude" headerText="Latitude" width="120" textAlign="center" />
              <ColumnDirective field="longitude" headerText="Longitude" width="100" textAlign="center" />
              <ColumnDirective
                field="id"
                headerText="Action"
                width="80"
                textAlign="center"
                template={locationsEditTemplate}
                isPrimaryKey={true}
              />
            </ColumnsDirective>
            <Inject services={[Page, Sort, Filter, Group, Search, Toolbar]} />
          </GridComponent>
        </DebugBoundary>
      </Box>

      {/* Modal */}
      <Dialog open={isModalOpen} onClose={closeModal} maxWidth="sm" fullWidth>
        <DialogTitle>{isAddMode ? 'Add New Location' : 'Edit Location'}</DialogTitle>
        <DialogContent dividers>
          {/* 🟢 Site Assessment ID (always on top) */}
          <FormControl fullWidth margin="dense">
            <InputLabel>Select Site Assessment</InputLabel>
            <Select
              name="siteAssessmentId"
              value={formData.siteAssessmentId || ''}
              onChange={e =>
                setFormData(prev => ({
                  ...prev,
                  siteAssessmentId: e.target.value, // stores only ID
                }))
              }
              label="Select Site Assessment">
              <MenuItem value="" disabled>
                Select Site Assessment
              </MenuItem>
              {siteIds.map(site => (
                <MenuItem key={site.id} value={site.id}>
                  {site.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* 🔹 Other fields */}
          {['destinationName', 'custCode', 'latitude', 'longitude', 'customerName'].map(field => (
            <TextField
              key={field}
              margin="dense"
              name={field}
              label={field.replace(/([A-Z])/g, ' $1')}
              value={formData[field] || ''}
              onChange={handleInputChange}
              fullWidth
            />
          ))}
        </DialogContent>

        <DialogActions>
          <Button onClick={closeModal}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>
            {isAddMode ? 'Add' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default MetadataSettings;
